import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Leaf, 
  Drumstick, 
  Gift, 
  Copy, 
  Check, 
  Loader2,
  Users,
  ArrowRight,
  CheckCircle2,
  Star,
  Calendar,
  Package,
  Clock,
  Sparkles,
  AlertTriangle,
  ShoppingBag,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'weekly',
    name: 'Weekly Plan',
    priceVeg: 60,
    priceNonVeg: 70,
    priceMix: 65,
    period: 'week',
    icon: Calendar,
    description: '7-day meal subscription',
    features: ['Daily meals for 7 days', 'Free home delivery', '3 Chapathis with Curry or Fry daily', 'A Boiled Egg daily', 'Fruits & vegetables included'],
    popular: false
  },
  {
    id: 'monthly',
    name: 'Monthly Plan',
    priceVeg: 229,
    priceNonVeg: 249,
    priceMix: 235,
    period: 'month',
    icon: Star,
    description: 'Full month meal subscription',
    features: ['Daily meals for 30 days', 'Free home delivery', '3 Chapathis with Curry or Fry daily', 'A Boiled Egg daily', 'Best value!'],
    popular: true
  },
  {
    id: 'single',
    name: 'Single Order',
    priceVeg: 15,
    priceNonVeg: 17,
    period: 'one-time',
    icon: ShoppingBag,
    description: 'Order a single meal',
    notice: 'Must order 24 hours in advance',
    features: [
      'Biryani or Rice with Curry',
      '3 Chapathis with Curry or Fry',
      'Boiled Egg',
      'Vegetable Salad',
      'Fruit Salad'
    ],
    deliveryNote: 'Free delivery under 3 miles. £3 over 3 miles.',
    popular: false
  }
];

export default function Subscribe() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [mealType, setMealType] = useState('non-veg');
  const [address, setAddress] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [over3Miles, setOver3Miles] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/my`, { withCredentials: true });
      setSubscription(response.data);
    } catch (error) {
      // Subscription fetch failed
    }
  }, []);

  const fetchReferrals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/referrals/my`, { withCredentials: true });
      setReferralData(response.data);
    } catch (error) {
      // Referrals fetch failed
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) {
      fetchSubscription();
      fetchReferrals();
    }
  }, [user, authLoading, fetchSubscription, fetchReferrals]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!address.trim()) {
      toast.error('Please enter your delivery address');
      return;
    }

    // Build WhatsApp message with order details
    const planName = currentPlan?.name || selectedPlan;
    const mealLabel = mealType === 'mix' ? 'Mix & Match' : mealType === 'non-veg' ? 'Non-Vegetarian' : 'Vegetarian';
    const deliveryInfo = selectedPlan === 'single' && over3Miles ? ' (over 3 miles - +£3 delivery)' : '';
    
    let message = `Hi ANAN's Kitchen! I'd like to subscribe.\n\n`;
    message += `Plan: ${planName}\n`;
    message += `Meal Type: ${mealLabel}\n`;
    message += `Price: £${finalPrice}/${currentPlan?.period}${deliveryInfo}\n`;
    message += `Delivery Address: ${address}\n`;
    if (referralCode) message += `Referral Code: ${referralCode}\n`;
    message += `\nPlease share your bank details for payment. Thank you!`;

    const whatsappUrl = `https://wa.me/4407827893614?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Redirecting you to WhatsApp to complete your order!');
  };

  const copyReferralCode = () => {
    if (referralData?.referral_code) {
      navigator.clipboard.writeText(referralData.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const currentPlan = PLANS.find(p => p.id === selectedPlan);
  const currentPrice = currentPlan ? (mealType === 'mix' ? (currentPlan.priceMix || currentPlan.priceVeg) : mealType === 'non-veg' ? currentPlan.priceNonVeg : currentPlan.priceVeg) : 0;
  const deliveryCharge = selectedPlan === 'single' && over3Miles ? 3 : 0;
  const referralDiscount = referralCode && selectedPlan !== 'single' ? 10 : 0;
  const finalPrice = Math.max(currentPrice - referralDiscount + deliveryCharge, 0);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF6F0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#E05D3A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF6F0] py-12" data-testid="subscribe-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {!subscription && (
            <div className="bg-[#2C402E] text-white rounded-2xl p-4 mb-8 max-w-2xl mx-auto" data-testid="early-bird-banner">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-semibold">Subscriptions start 15th June</span>
              </div>
            </div>
          )}
          <Badge className="bg-[#E05D3A]/10 text-[#E05D3A] mb-4">Choose Your Plan</Badge>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C402E] mb-4" data-testid="subscribe-title">
            {subscription ? 'Your Subscription' : "Subscribe to ANAN's Kitchen"}
          </h1>
          <p className="text-[#4A4A4A] max-w-2xl mx-auto">
            {subscription 
              ? 'Manage your subscription and referral rewards'
              : 'Fresh, healthy meals delivered to your door in Bristol'}
          </p>
        </div>

        {/* Meal Type Toggle - Show before plans */}
        {!subscription && (
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-full p-1 shadow-lg inline-flex">
              <button
                onClick={() => setMealType('veg')}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                  mealType === 'veg' ? 'bg-[#2C402E] text-white' : 'text-[#4A4A4A] hover:text-[#2C402E]'
                }`}
              >
                <Leaf className="w-4 h-4" /> Vegetarian
              </button>
              <button
                onClick={() => setMealType('non-veg')}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                  mealType === 'non-veg' ? 'bg-[#E05D3A] text-white' : 'text-[#4A4A4A] hover:text-[#E05D3A]'
                }`}
              >
                <Drumstick className="w-4 h-4" /> Non-Vegetarian
              </button>
              {selectedPlan !== 'single' && (
                <button
                  onClick={() => setMealType('mix')}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                    mealType === 'mix' ? 'bg-[#D4AF37] text-white' : 'text-[#4A4A4A] hover:text-[#D4AF37]'
                  }`}
                >
                  Mix & Match
                </button>
              )}
            </div>
          </div>
        )}

        {/* Plan Selection Cards */}
        {!subscription && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {PLANS.map((plan) => {
              const Icon = plan.icon;
              const price = mealType === 'mix' ? (plan.priceMix || plan.priceVeg) : mealType === 'non-veg' ? plan.priceNonVeg : plan.priceVeg;
              return (
                <Card 
                  key={plan.id}
                  className={`relative cursor-pointer transition-all hover:shadow-lg ${
                    selectedPlan === plan.id 
                      ? 'ring-2 ring-[#E05D3A] shadow-lg' 
                      : 'hover:border-[#E05D3A]/30'
                  }`}
                  onClick={() => setSelectedPlan(plan.id)}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-[#E05D3A] text-white">Most Popular</Badge>
                    </div>
                  )}
                  <CardHeader className="text-center pb-2">
                    <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${
                      selectedPlan === plan.id ? 'bg-[#E05D3A] text-white' : 'bg-[#E05D3A]/10 text-[#E05D3A]'
                    }`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <CardTitle className="font-serif text-xl text-[#2C402E]">{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                    {plan.notice && (
                      <Badge className="bg-amber-100 text-amber-800 mt-2">
                        <Clock className="w-3 h-3 mr-1" /> {plan.notice}
                      </Badge>
                    )}
                  </CardHeader>
                  <CardContent className="text-center">
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-[#2C402E]">&pound;{price}</span>
                      <span className="text-[#4A4A4A]">/{plan.period}</span>
                    </div>
                    {plan.deliveryNote && (
                      <p className="text-xs text-[#E05D3A] font-medium mb-3">{plan.deliveryNote}</p>
                    )}
                    <ul className="text-left space-y-2">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-[#2C402E] mt-0.5 flex-shrink-0" />
                          <span className="text-[#4A4A4A]">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {subscription ? (
              <Card data-testid="active-subscription-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-serif flex items-center gap-2 text-[#2C402E]">
                      <CheckCircle2 className="w-5 h-5 text-[#2C402E]" />
                      Active Subscription
                    </CardTitle>
                    <Badge className="bg-[#2C402E]/10 text-[#2C402E]">{subscription.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-[#4A4A4A]">Plan</p>
                      <p className="font-medium capitalize text-[#2C402E]">{subscription.plan_type || 'Monthly'} Plan</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#4A4A4A]">Meal Type</p>
                      <p className="font-medium flex items-center gap-2 text-[#2C402E]">
                        {subscription.meal_type === 'veg' ? (
                          <><Leaf className="w-4 h-4 text-[#2C402E]" /> Vegetarian</>
                        ) : (
                          <><Drumstick className="w-4 h-4 text-[#E05D3A]" /> Non-Vegetarian</>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[#4A4A4A]">Price</p>
                      <p className="font-medium text-[#2C402E]">
                        &pound;{subscription.final_price}/{subscription.plan_type === 'single' ? 'order' : subscription.plan_type || 'month'}
                        {subscription.discount > 0 && (
                          <span className="text-[#2C402E] text-sm ml-2">(&pound;{subscription.discount} off)</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-[#4A4A4A]">Delivery Address</p>
                    <p className="font-medium text-[#2C402E]">{subscription.address}</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card data-testid="subscription-form-card">
                <CardHeader>
                  <CardTitle className="font-serif flex items-center gap-2 text-[#2C402E]">
                    <Package className="w-5 h-5 text-[#E05D3A]" />
                    {currentPlan?.name} Details
                  </CardTitle>
                  <CardDescription>
                    Enter your delivery details to proceed
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubscribe} className="space-y-6">
                    {/* Address */}
                    <div className="space-y-2">
                      <Label htmlFor="address">Delivery Address</Label>
                      <Textarea
                        id="address"
                        placeholder="Enter your full delivery address in Bristol"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        rows={3}
                        required
                        data-testid="address-input"
                      />
                    </div>

                    {/* Distance checkbox for single order */}
                    {selectedPlan === 'single' && (
                      <div className="flex items-start gap-3 p-4 bg-[#FAF6F0] rounded-xl">
                        <Checkbox
                          id="over3miles"
                          checked={over3Miles}
                          onCheckedChange={(checked) => setOver3Miles(checked)}
                          data-testid="over-3-miles-checkbox"
                        />
                        <div>
                          <Label htmlFor="over3miles" className="text-sm font-medium text-[#2C402E] cursor-pointer">
                            My address is over 3 miles away (+£3 delivery)
                          </Label>
                          <p className="text-xs text-[#4A4A4A] mt-1">Free delivery for addresses within 3 miles</p>
                        </div>
                      </div>
                    )}

                    {/* Referral Code (only for weekly/monthly) */}
                    {selectedPlan !== 'single' && (
                      <div className="space-y-2">
                        <Label htmlFor="referralCode">Referral Code (Optional)</Label>
                        <div className="relative">
                          <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A4A4A]" />
                          <Input
                            id="referralCode"
                            placeholder="Enter referral code for £10 off"
                            value={referralCode}
                            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                            className="pl-10"
                            data-testid="referral-code-input"
                          />
                        </div>
                      </div>
                    )}

                    {/* Mix & Match Notice */}
                    {mealType === 'mix' && (
                      <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4 flex items-start gap-3">
                        <Clock className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-[#2C402E] font-medium">Mix & Match Plan</p>
                          <p className="text-xs text-[#4A4A4A] mt-1">
                            Switch between Veg and Non-Veg anytime! Please notify us <strong>3 days in advance</strong> if you wish to change your menu.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Allergen Notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="allergen-notice">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-amber-800 font-medium">Allergen Information</p>
                        <p className="text-xs text-amber-700 mt-1">
                          Our meals contain <strong>eggs, gluten (wheat), dairy, nuts, peanuts, and mustard</strong>. 
                          <a href="/allergens" className="text-[#E05D3A] font-semibold hover:underline ml-1">View full allergen details</a>
                        </p>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-[#25D366] hover:bg-[#1da851]"
                      data-testid="subscribe-submit-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      {selectedPlan === 'single' ? 'Order via WhatsApp' : 'Subscribe via WhatsApp'} - &pound;{finalPrice}
                      {selectedPlan !== 'single' && `/${currentPlan?.period}`}
                      {deliveryCharge > 0 && ' (incl. £3 delivery)'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Referral Section */}
            {user && referralData && (
              <Card data-testid="referral-card">
                <CardHeader>
                  <CardTitle className="font-serif flex items-center gap-2 text-[#2C402E]">
                    <Gift className="w-5 h-5 text-[#E05D3A]" />
                    Your Referral Program
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-[#FAF6F0] rounded-xl p-4">
                    <p className="text-sm text-[#4A4A4A] mb-2">Your Referral Code</p>
                    <div className="flex items-center gap-3">
                      <code className="text-2xl font-bold text-[#E05D3A] tracking-wider">
                        {referralData.referral_code}
                      </code>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={copyReferralCode}
                        className="border-[#2C402E]/20 hover:border-[#E05D3A]"
                        data-testid="copy-referral-btn"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-[#4A4A4A]">Referral Progress</span>
                      <span className="font-semibold text-[#2C402E]">{referralData.referral_count}/25</span>
                    </div>
                    <div className="w-full bg-[#2C402E]/10 rounded-full h-3">
                      <div 
                        className="bg-[#E05D3A] h-3 rounded-full transition-all"
                        style={{ width: `${Math.min((referralData.referral_count / 25) * 100, 100)}%` }}
                      />
                    </div>
                    {referralData.free_month_eligible ? (
                      <p className="text-[#2C402E] text-sm mt-2 flex items-center gap-1">
                        <Star className="w-4 h-4 text-[#D4AF37]" />
                        You've earned FREE food for a month!
                      </p>
                    ) : (
                      <p className="text-[#4A4A4A] text-sm mt-2">
                        {25 - referralData.referral_count} more referrals for free food!
                      </p>
                    )}
                  </div>

                  {referralData.referrals?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-3 text-[#2C402E]">Recent Referrals</p>
                      <div className="space-y-2">
                        {referralData.referrals.slice(0, 5).map((ref, index) => (
                          <div key={index} className="flex items-center justify-between py-2 border-b border-[#2C402E]/10">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-[#4A4A4A]" />
                              <span className="text-sm text-[#2C402E]">{ref.referred_name}</span>
                            </div>
                            <Badge variant="secondary" className="bg-[#2C402E]/10 text-[#2C402E]">
                              Subscribed
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {!subscription && currentPlan && (
              <Card className="bg-gradient-to-br from-[#2C402E] to-[#1a2a1c] text-white">
                <CardContent className="pt-6">
                  <p className="text-[#FAF6F0]/70 text-sm mb-2">{currentPlan.name} ({mealType === 'non-veg' ? 'Non-Veg' : 'Veg'})</p>
                  <div className="text-5xl font-black mb-1">&pound;{currentPrice}</div>
                  <p className="text-[#FAF6F0]/50 text-sm mb-1">per {currentPlan.period}</p>
                  {deliveryCharge > 0 && (
                    <p className="text-[#D4AF37] text-sm mb-3">+ £3 delivery</p>
                  )}
                  {referralDiscount > 0 && (
                    <p className="text-[#D4AF37] text-sm mb-3">- £10 referral discount</p>
                  )}
                  <ul className="space-y-2 text-sm mt-4">
                    {currentPlan.features.slice(0, 5).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-[#D4AF37]" /> {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-lg text-[#2C402E]">Referral Benefits</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#E05D3A]/10 flex items-center justify-center flex-shrink-0">
                    <Gift className="w-4 h-4 text-[#E05D3A]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C402E]">&pound;10 Off</p>
                    <p className="text-sm text-[#4A4A4A]">For each friend who subscribes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <Star className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C402E]">Free Month!</p>
                    <p className="text-sm text-[#4A4A4A]">Refer 25 friends for free food</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {selectedPlan === 'single' && (
              <Card className="border-2 border-[#E05D3A]/20 bg-[#E05D3A]/5">
                <CardHeader>
                  <CardTitle className="font-serif text-lg flex items-center gap-2 text-[#2C402E]">
                    <ShoppingBag className="w-5 h-5 text-[#E05D3A]" />
                    Single Order Info
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-amber-100 text-amber-800 p-3 rounded-lg mb-4 text-sm font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Must be ordered 24 hours in advance
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-[#2C402E]">
                      <span>Vegetarian</span>
                      <span className="font-bold">&pound;12</span>
                    </div>
                    <div className="flex justify-between text-[#2C402E]">
                      <span>Non-Vegetarian</span>
                      <span className="font-bold">&pound;15</span>
                    </div>
                    <div className="border-t border-[#2C402E]/10 pt-2 mt-2">
                      <p className="text-xs text-[#4A4A4A]">Free delivery within 3 miles</p>
                      <p className="text-xs text-[#4A4A4A]">£3 delivery charge over 3 miles</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Workplace Delivery Card */}
            {selectedPlan !== 'single' && !subscription && (
              <Card className="bg-[#2C402E] text-white" data-testid="workplace-delivery-card">
                <CardContent className="pt-6">
                  <p className="font-serif font-semibold text-lg mb-2">Workplace Delivery</p>
                  <p className="text-[#FAF6F0]/80 text-sm mb-4">
                    We also deliver to workplaces in Bristol in the same slots (12-3 PM &amp; 5-10 PM).
                  </p>
                  <div className="flex gap-3">
                    <div className="bg-white/10 rounded-lg px-4 py-2 text-center flex-1">
                      <p className="text-[#D4AF37] font-bold text-lg">+£20</p>
                      <p className="text-xs text-[#FAF6F0]/60">weekly</p>
                    </div>
                    <div className="bg-white/10 rounded-lg px-4 py-2 text-center flex-1">
                      <p className="text-[#D4AF37] font-bold text-lg">+£50</p>
                      <p className="text-xs text-[#FAF6F0]/60">monthly</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
